import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import TripCard from './components/TripCard';
import FilterControls from './components/FilterControls';
import TripSummaryStats from './components/TripSummaryStats';
import EmptyState from './components/EmptyState';
import Icon from '../../components/AppIcon';
import ConfirmationModal from '../../components/ConfirmationModal';

import {
  HexagonPattern,
  HandDrawnConnector,
  AngularAccent,
  GridDots,
  HandDrawnUnderline,
} from './components/GeometricPatterns';

// ─── Mock / seed data ────────────────────────────────────────────────────────
const SEED_TRIPS = [
  {
    id: 'trip-1',
    date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Oslo Sentrum',
    destination: 'Drammen',
    vehicleType: 'ev',
    distance: 65,
    duration: '1t 15min',
    originalCost: 121,
    totalCost: 60.5,
    discount: 60.5,
    discountPercentage: 50,
    tollPoints: [
      { name: 'Festningstunnelen', location: 'Oslo sentrum', cost: 14.5, originalCost: 29 },
      { name: 'Smestad', location: 'Oslo vest', cost: 11, originalCost: 22 },
      { name: 'Lysaker', location: 'Bærum', cost: 12.5, originalCost: 25 },
      { name: 'Asker', location: 'Asker sentrum', cost: 9.5, originalCost: 19 },
      { name: 'Drammen', location: 'Drammen øst', cost: 13, originalCost: 26 },
    ],
  },
  {
    id: 'trip-2',
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Bergen Sentrum',
    destination: 'Flesland Lufthavn',
    vehicleType: 'hybrid',
    distance: 18,
    duration: '28min',
    originalCost: 48,
    totalCost: 36,
    discount: 12,
    discountPercentage: 25,
    tollPoints: [
      { name: 'Nygårdstunnelen', location: 'Bergen sentrum', cost: 15, originalCost: 20 },
      { name: 'Fjøsanger', location: 'Bergen sør', cost: 12, originalCost: 16 },
      { name: 'Flesland', location: 'Flesland', cost: 9, originalCost: 12 },
    ],
  },
  {
    id: 'trip-3',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Trondheim',
    destination: 'Stjørdal',
    vehicleType: 'standard',
    distance: 42,
    duration: '45min',
    originalCost: 74,
    totalCost: 74,
    discount: 0,
    discountPercentage: 0,
    tollPoints: [
      { name: 'Ranheim', location: 'Trondheim øst', cost: 28, originalCost: 28 },
      { name: 'Hommelvik', location: 'Malvik', cost: 22, originalCost: 22 },
      { name: 'Stjørdal', location: 'Stjørdal sentrum', cost: 24, originalCost: 24 },
    ],
  },
  {
    id: 'trip-4',
    date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Stavanger',
    destination: 'Sandnes',
    vehicleType: 'ev',
    distance: 14,
    duration: '22min',
    originalCost: 36,
    totalCost: 18,
    discount: 18,
    discountPercentage: 50,
    tollPoints: [
      { name: 'Gandsfjordbrua', location: 'Stavanger sør', cost: 10, originalCost: 20 },
      { name: 'Sandnes nord', location: 'Sandnes', cost: 8, originalCost: 16 },
    ],
  },
  {
    id: 'trip-5',
    date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Kristiansand',
    destination: 'Mandal',
    vehicleType: 'hybrid',
    distance: 45,
    duration: '50min',
    originalCost: 56,
    totalCost: 42,
    discount: 14,
    discountPercentage: 25,
    tollPoints: [
      { name: 'Vågsbygd', location: 'Kristiansand vest', cost: 18, originalCost: 24 },
      { name: 'Søgne', location: 'Søgne', cost: 14, originalCost: 18 },
      { name: 'Mandal', location: 'Mandal øst', cost: 10, originalCost: 14 },
    ],
  },
  {
    id: 'trip-6',
    date: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000)?.toISOString(),
    departure: 'Tromsø Sentrum',
    destination: 'Tromsø Lufthavn',
    vehicleType: 'standard',
    distance: 8,
    duration: '15min',
    originalCost: 32,
    totalCost: 32,
    discount: 0,
    discountPercentage: 0,
    tollPoints: [
      { name: 'Tromsøysundtunnelen', location: 'Tromsø', cost: 32, originalCost: 32 },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const loadTripsFromStorage = () => {
  try {
    const raw = localStorage.getItem('tollCalculatorHistory');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed?.length > 0) return parsed;
    }
  } catch (_) {}
  return [];
};

const filterTrips = (trips, { vehicle, period, sort }) => {
  let result = [...trips];

  if (vehicle && vehicle !== 'all') {
    result = result?.filter((t) => t?.vehicleType === vehicle);
  }

  if (period && period !== 'all') {
    const days = { '7d': 7, '30d': 30, '90d': 90 }?.[period] || 9999;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    result = result?.filter((t) => new Date(t?.date)?.getTime() >= cutoff);
  }

  result?.sort((a, b) => {
    const ta = new Date(a?.date)?.getTime();
    const tb = new Date(b?.date)?.getTime();
    return sort === 'oldest' ? ta - tb : tb - ta;
  });

  return result;
};

// ─── Page Header ─────────────────────────────────────────────────────────────
const PageHeader = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      ref={ref}
      className="relative overflow-hidden mb-8 md:mb-10"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(-20px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 700ms cubic-bezier(0.34, 1.2, 0.64, 1), opacity 600ms ease-out',
      }}
    >
      <HexagonPattern className="top-0 right-0" opacity={0.05} />
      <GridDots className="bottom-0 left-0" rows={3} cols={10} opacity={0.08} />
      <AngularAccent className="bottom-0 right-8" width={80} height={80} color="#FF5722" />

      <div
        style={{
          background: 'var(--color-card)',
          border: '2px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'clamp(16px, 3vw, 28px) clamp(16px, 3vw, 32px)',
          borderLeft: '4px solid var(--color-primary)',
          position: 'relative',
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div
            style={{
              width: 48,
              height: 48,
              background: 'rgba(0,79,152,0.12)',
              border: '2px solid rgba(0,79,152,0.25)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="History" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <h1
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: 'clamp(24px, 4vw, 36px)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.1,
                  marginBottom: '4px',
                }}
              >
                Reisehistorikk
              </h1>
              <HandDrawnUnderline width={180} className="mb-2" />
            </div>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
                color: 'var(--color-muted-foreground)',
                maxWidth: '480px',
                lineHeight: 1.6,
              }}
            >
              Kronologisk oversikt over alle bompasseringer med kostnader, rabatter og ruteinformasjon.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Timeline connector ───────────────────────────────────────────────────────
const TimelineConnector = ({ index }) => (
  <div
    className="relative flex justify-center"
    style={{ height: '40px', marginLeft: index % 2 === 1 ? '24px' : '0' }}
  >
    <HandDrawnConnector className="top-0" />
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const TollHistory = () => {
  const [allTrips, setAllTrips] = useState(loadTripsFromStorage);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [filters, setFilters] = useState({ vehicle: 'all', period: 'all', sort: 'newest' });
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, tripId: null, tripLabel: '' });

  useEffect(() => {
    const timer = setTimeout(() => {
      const stored = loadTripsFromStorage();
      setFilteredTrips(stored);
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setFilteredTrips(filterTrips(allTrips, filters));
  }, [allTrips, filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleDeleteRequest = (trip) => {
    const label = `${trip?.departure} → ${trip?.destination}`;
    setDeleteModal({ isOpen: true, tripId: trip?.id, tripLabel: label });
  };

  const handleDeleteConfirm = () => {
    const updatedTrips = allTrips?.filter((t) => t?.id !== deleteModal?.tripId);
    try {
      localStorage.setItem('tollCalculatorHistory', JSON.stringify(updatedTrips));
    } catch (_) {}
    setAllTrips(updatedTrips);
    setDeleteModal({ isOpen: false, tripId: null, tripLabel: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, tripId: null, tripLabel: '' });
  };

  return (
    <>
      <Helmet>
        <title>Reisehistorikk — Veiassistent</title>
        <meta
          name="description"
          content="Se din komplette bompasseringshistorikk med kostnader, rabatter og ruteinformasjon."
        />
      </Helmet>
      <Header />

      <ConfirmationModal
        isOpen={deleteModal?.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Slett reise"
        message={`Er du sikker på at du vil slette reisen «${deleteModal?.tripLabel}»? Denne handlingen kan ikke angres.`}
        consequence="Reisen og alle tilknyttede bompasseringer vil bli permanent slettet fra historikken din."
        confirmLabel="Slett reise"
        cancelLabel="Avbryt"
        isDanger={true}
      />

      <main
        className="min-h-screen"
        style={{
          background: 'var(--color-background)',
          paddingTop: '84px',
          paddingBottom: '64px',
        }}
      >
        {/* Ambient background geometry */}
        <div
          className="fixed pointer-events-none"
          style={{ top: '120px', left: '-80px', opacity: 0.03, zIndex: 0 }}
          aria-hidden="true"
        >
          <HexagonPattern opacity={1} />
        </div>
        <div
          className="fixed pointer-events-none"
          style={{ bottom: '80px', right: '-60px', opacity: 0.03, zIndex: 0 }}
          aria-hidden="true"
        >
          <HexagonPattern opacity={1} />
        </div>

        <div
          className="relative z-10 max-w-[860px] mx-auto px-4 md:px-6 lg:px-8"
        >
          {/* Page header */}
          <PageHeader />

          {/* Summary stats */}
          {allTrips?.length > 0 && (
            <div className="mb-6">
              <TripSummaryStats trips={allTrips} />
            </div>
          )}

          {/* Filter controls */}
          <div className="mb-8">
            <FilterControls
              onFilterChange={handleFilterChange}
              totalTrips={filteredTrips?.length}
            />
          </div>

          {/* Timeline */}
          {isLoading ? (
            <EmptyState type="loading" />
          ) : filteredTrips?.length === 0 ? (
            <EmptyState type="empty" />
          ) : (
            <div>
              {filteredTrips?.map((trip, index) => (
                <div key={trip?.id || index}>
                  <TripCard trip={trip} index={index} onDelete={handleDeleteRequest} />
                  {index < filteredTrips?.length - 1 && (
                    <TimelineConnector index={index} />
                  )}
                </div>
              ))}

              {/* End of timeline marker */}
              <div
                className="flex items-center gap-4 mt-8 pt-6"
                style={{ borderTop: '1px solid var(--color-border)' }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'var(--color-muted-foreground)',
                    opacity: 0.4,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '12px',
                    color: 'var(--color-muted-foreground)',
                    opacity: 0.6,
                  }}
                >
                  {filteredTrips?.length === allTrips?.length
                    ? `Viser alle ${allTrips?.length} reiser`
                    : `Viser ${filteredTrips?.length} av ${allTrips?.length} reiser`}
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default TollHistory;
